<template>
  <div class="world-map">
    <svg :viewBox="`0 0 ${W} ${H}`" class="wm-svg" role="img" aria-label="World map">
      <rect x="0" y="0" :width="W" :height="H" class="wm-sea" />

      <!-- graticule: every 30° longitude, every 20° latitude; equator brighter -->
      <g class="wm-grid">
        <line v-for="lon in gridLons" :key="'gx' + lon" :x1="x(lon)" y1="0" :x2="x(lon)" :y2="H" vector-effect="non-scaling-stroke" />
        <line v-for="lat in gridLats" :key="'gy' + lat" x1="0" :y1="y(lat)" :x2="W" :y2="y(lat)" :class="{ eq: lat === 0 }" vector-effect="non-scaling-stroke" />
      </g>
      <g class="wm-grid-labels">
        <text v-for="lat in gridLats" :key="'gl' + lat" x="1.5" :y="y(lat) - 1">{{ lat }}°</text>
      </g>

      <!-- background dots (e.g. the city pool) -->
      <g class="wm-dots">
        <circle
          v-for="(d, i) in dots"
          :key="'d' + i"
          :cx="x(d.lon)"
          :cy="y(d.lat)"
          :r="d.r || 1"
          :class="'dot-' + (d.variant || 'muted')"
        ><title v-if="d.title">{{ d.title }}</title></circle>
      </g>

      <!-- planetary lines, one polyline per unbroken run of points -->
      <g class="wm-lines">
        <g
          v-for="c in visibleCurves"
          :key="c.key"
          :class="['wm-curve', { dim: hoverPlanet && hoverPlanet !== c.planet }]"
          @mouseenter="hoverPlanet = c.planet"
          @mouseleave="hoverPlanet = ''"
        >
          <polyline
            v-for="(seg, si) in c.segments"
            :key="si"
            :points="seg"
            fill="none"
            :stroke="planetColor(c.planet)"
            :stroke-dasharray="angleDash(c.angle)"
            vector-effect="non-scaling-stroke"
            class="wm-line"
          ><title>{{ c.planet }} {{ c.angle }}</title></polyline>
          <text v-if="c.angle === 'MC' && c.labelX != null" :x="c.labelX + 1" y="5" :fill="planetColor(c.planet)" class="wm-line-label">{{ glyph(c.planet) }}MC</text>
        </g>
      </g>

      <!-- birthplace -->
      <g v-if="birth && birth.lat != null && birth.lon != null" class="wm-birth">
        <circle :cx="x(birth.lon)" :cy="y(birth.lat)" r="3.2" class="birth-ring" />
        <circle :cx="x(birth.lon)" :cy="y(birth.lat)" r="1.4" class="birth-core" />
        <title>{{ birth.title || 'Birthplace' }}</title>
      </g>

      <!-- places (numbered) -->
      <g class="wm-places">
        <g
          v-for="p in places"
          :key="'p' + (p.key || p.label)"
          class="wm-place"
          :class="{ active: activeKey && activeKey === p.key }"
          @click="$emit('place-click', p)"
        >
          <circle :cx="x(p.lon)" :cy="y(p.lat)" r="3.6" class="place-pin" />
          <text :x="x(p.lon)" :y="y(p.lat) + 1.3" class="place-num">{{ p.label }}</text>
          <title>{{ p.title || p.label }}</title>
        </g>
      </g>
    </svg>

    <!-- legend: planets toggle their lines; angles explain the dash style -->
    <div v-if="curves.length" class="wm-legend">
      <button
        v-for="pl in planetsInCurves"
        :key="pl"
        :class="['wm-planet', { off: hidden.includes(pl) }]"
        @click="togglePlanet(pl)"
        @mouseenter="hoverPlanet = pl"
        @mouseleave="hoverPlanet = ''"
      >
        <span class="wm-swatch" :style="{ background: planetColor(pl) }"></span>{{ glyph(pl) }} {{ pl }}
      </button>
      <span class="wm-angles">
        <span><svg width="22" height="6"><line x1="0" y1="3" x2="22" y2="3" stroke="currentColor" stroke-width="1.6" /></svg> MC / ASC</span>
        <span><svg width="22" height="6"><line x1="0" y1="3" x2="22" y2="3" stroke="currentColor" stroke-width="1.6" stroke-dasharray="2 2" /></svg> IC</span>
        <span><svg width="22" height="6"><line x1="0" y1="3" x2="22" y2="3" stroke="currentColor" stroke-width="1.6" stroke-dasharray="5 3" /></svg> DSC</span>
      </span>
    </div>
  </div>
</template>

<script>
// Equirectangular world map in plain SVG (no tiles, no geo data): longitude
// −180..180 → x, latitude +80..−80 → y. Draws astrocartography lines (MC/IC as
// meridians, ASC/DSC as sampled curves), numbered place pins, the birthplace,
// and optional background dots (the city pool, which also gives the eye the
// shape of the inhabited world).
const W = 360
const LAT_MAX = 80
const H = LAT_MAX * 2

const PLANET_COLORS = {
  Sun: '#f8bf71', Moon: '#d6dcff', Mercury: '#22d3ee', Venus: '#f472b6', Mars: '#ff6b6b',
  Jupiter: '#a78bfa', Saturn: '#94a3b8', Uranus: '#34d399', Neptune: '#60a5fa', Pluto: '#c084fc'
}
const GLYPHS = { Sun: '☉', Moon: '☽', Mercury: '☿', Venus: '♀', Mars: '♂', Jupiter: '♃', Saturn: '♄', Uranus: '♅', Neptune: '♆', Pluto: '♇' }
const PLANET_ORDER = Object.keys(PLANET_COLORS)

export default {
  name: 'WorldMap',
  props: {
    // [{ planet, angle: 'MC'|'IC'|'ASC'|'DSC', points: [{ lat, lon }] }]
    curves: { type: Array, default: () => [] },
    // [{ key, lat, lon, label, title }]
    places: { type: Array, default: () => [] },
    // { lat, lon, title } | null
    birth: { type: Object, default: null },
    // [{ lat, lon, title, variant: 'muted'|'active'|'warn'|'draft', r }]
    dots: { type: Array, default: () => [] },
    activeKey: { type: String, default: '' }
  },
  emits: ['place-click'],
  data() {
    return {
      W, H,
      gridLons: [-150, -120, -90, -60, -30, 0, 30, 60, 90, 120, 150],
      gridLats: [60, 40, 20, 0, -20, -40, -60],
      hidden: [],
      hoverPlanet: ''
    }
  },
  computed: {
    planetsInCurves() {
      const set = new Set(this.curves.map(c => c.planet).filter(Boolean))
      return [...set].sort((a, b) => this.rank(a) - this.rank(b))
    },
    // Each curve split into unbroken runs: a new run starts where the
    // longitude wraps (jump > 180°) or, on the sampled ASC/DSC curves, where
    // samples are missing (latitude gap — the planet never rises there).
    // MC/IC are two-point meridians, so the gap rule never applies to them.
    visibleCurves() {
      return this.curves
        .filter(c => !this.hidden.includes(c.planet))
        .map((c, i) => {
          const sampled = c.angle === 'ASC' || c.angle === 'DSC'
          const segs = []
          let cur = []
          let prev = null
          ;(c.points || []).forEach(pt => {
            const lat = Number(pt.lat)
            const lon = Number(pt.lon)
            if (!isFinite(lat) || !isFinite(lon)) return
            if (prev && (Math.abs(lon - prev.lon) > 180 || (sampled && Math.abs(lat - prev.lat) > 5.5))) {
              if (cur.length > 1) segs.push(cur)
              cur = []
            }
            cur.push(`${this.x(lon).toFixed(2)},${this.y(lat).toFixed(2)}`)
            prev = { lat, lon }
          })
          if (cur.length > 1) segs.push(cur)
          const first = (c.points || [])[0]
          return {
            key: `${c.planet}-${c.angle}-${i}`,
            planet: c.planet,
            angle: c.angle,
            segments: segs.map(s => s.join(' ')),
            labelX: c.angle === 'MC' && first ? this.x(Number(first.lon)) : null
          }
        })
    }
  },
  methods: {
    x(lon) { return Math.max(0, Math.min(W, Number(lon) + 180)) },
    y(lat) { return Math.max(0, Math.min(H, LAT_MAX - Number(lat))) },
    rank(p) { const i = PLANET_ORDER.indexOf(p); return i === -1 ? 99 : i },
    planetColor(p) { return PLANET_COLORS[p] || '#8b8b9e' },
    glyph(p) { return GLYPHS[p] || '' },
    angleDash(a) { return a === 'IC' ? '2 2' : (a === 'DSC' ? '5 3' : null) },
    togglePlanet(p) {
      this.hidden = this.hidden.includes(p) ? this.hidden.filter(h => h !== p) : [...this.hidden, p]
    }
  }
}
</script>

<style scoped>
.world-map { width: 100%; }
.wm-svg { width: 100%; height: auto; display: block; border-radius: 10px; border: 1px solid var(--panel-border); }
.wm-sea { fill: rgba(255, 255, 255, 0.02); }
.wm-grid line { stroke: rgba(255, 255, 255, 0.06); stroke-width: 1; }
.wm-grid line.eq { stroke: rgba(255, 255, 255, 0.16); }
.wm-grid-labels text { font-size: 3px; fill: rgba(255, 255, 255, 0.28); }

.dot-muted { fill: rgba(255, 255, 255, 0.22); }
.dot-active { fill: rgba(74, 222, 128, 0.75); }
.dot-warn { fill: rgba(255, 107, 107, 0.9); }
.dot-draft { fill: rgba(248, 191, 113, 0.85); }

.wm-curve { transition: opacity 0.15s ease; cursor: default; }
.wm-curve.dim { opacity: 0.15; }
.wm-line { stroke-width: 1.6px; opacity: 0.85; }
.wm-curve:hover .wm-line { stroke-width: 2.6px; opacity: 1; }
.wm-line-label { font-size: 3.4px; font-weight: 700; }

.birth-ring { fill: none; stroke: #fff; stroke-width: 0.7; }
.birth-core { fill: #fff; }

.wm-place { cursor: pointer; }
.place-pin { fill: var(--primary-color); stroke: #fff; stroke-width: 0.6; }
.wm-place.active .place-pin { fill: #f8bf71; }
.place-num { font-size: 3.6px; font-weight: 700; fill: #fff; text-anchor: middle; pointer-events: none; }

.wm-legend { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; margin-top: 10px; }
.wm-planet { display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; font-size: 11.5px; border-radius: 12px; border: 1px solid var(--panel-border); background: var(--field-bg); color: var(--text-primary); cursor: pointer; }
.wm-planet.off { opacity: 0.35; }
.wm-swatch { width: 9px; height: 9px; border-radius: 50%; display: inline-block; }
.wm-angles { display: inline-flex; gap: 12px; margin-left: auto; font-size: 11px; color: var(--text-secondary); }
.wm-angles span { display: inline-flex; align-items: center; gap: 4px; }
</style>
